<template>
  <!-- [matrah] Live WhatsApp-style rendering of one template language. -->
  <div class="wa-preview" :dir="direction">
    <div class="wa-preview-topbar" dir="ltr">
      <v-avatar size="28" color="white" class="mr-2">
        <img :src="logo" alt="" class="wa-avatar-img" />
      </v-avatar>
      <div class="wa-topbar-text">
        <div class="wa-topbar-title">{{ brand }}</div>
        <div class="wa-topbar-sub">Business account</div>
      </div>
      <v-spacer />
      <LanguageFlag :code="language.code" :size="18" />
      <span class="wa-topbar-lang">{{ languageName }}</span>
    </div>

    <div class="wa-chat">
      <div class="wa-bubble">
        <template v-if="header.type === 'TEXT' && header.text">
          <div class="wa-header-text">{{ header.text }}</div>
        </template>
        <template v-else-if="isMediaHeader">
          <div class="wa-media" :class="'wa-media-' + header.type.toLowerCase()">
            <img v-if="header.type === 'IMAGE' && header.example_url" :src="header.example_url" alt="Header image"
              class="wa-media-img" @error="mediaError = true" />
            <div v-else class="wa-media-placeholder">
              <v-icon :icon="mediaIcon" size="34" />
              <span>{{ mediaLabel }}</span>
            </div>
          </div>
        </template>

        <div class="wa-body" :class="{ 'wa-body-empty': !language.body }" v-html="bodyHtml"></div>

        <div v-if="language.footer" class="wa-footer">{{ language.footer }}</div>
        <div class="wa-time" dir="ltr">12:00</div>
      </div>

      <template v-if="ctaButtons.length">
        <div v-for="(btn, i) in ctaButtons" :key="'cta-' + i" class="wa-button wa-button-cta">
          <v-icon :icon="btn.type === 'URL' ? 'mdi-open-in-new' : 'mdi-phone'" size="16" class="mr-1" />
          <span>{{ btn.text || placeholderFor(btn) }}</span>
        </div>
      </template>
      <template v-if="quickReplies.length">
        <div class="wa-quick-replies">
          <div v-for="(btn, i) in quickReplies" :key="'qr-' + i" class="wa-button wa-button-qr">
            <v-icon icon="mdi-reply" size="16" class="mr-1" />
            <span>{{ btn.text || 'Quick reply' }}</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import LanguageFlag from './LanguageFlag.vue'
import logo from '@/assets/matrah-logo-transparent.png'
import { languageMeta, renderWhatsAppMarkup } from '@/utils/templateLanguages'

export default {
  name: 'WhatsAppPreview',
  components: { LanguageFlag },
  props: {
    language: { type: Object, required: true },
  },
  data: () => ({
    logo,
    brand: process.env.VUE_APP_NAME || 'Zakat Matrah',
    mediaError: false,
  }),
  computed: {
    meta() {
      return languageMeta(this.language.code)
    },
    languageName() {
      return this.meta.name
    },
    direction() {
      return this.meta.dir
    },
    header() {
      return this.language.header || { type: 'NONE' }
    },
    isMediaHeader() {
      return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(this.header.type)
    },
    mediaIcon() {
      return { IMAGE: 'mdi-image', VIDEO: 'mdi-play-circle', DOCUMENT: 'mdi-file-document' }[this.header.type]
    },
    mediaLabel() {
      return { IMAGE: 'Image', VIDEO: 'Video', DOCUMENT: 'Document' }[this.header.type]
    },
    bodyHtml() {
      if (!this.language.body) return 'Message body will appear here…'
      return renderWhatsAppMarkup(this.language.body, this.language.body_examples || [])
    },
    ctaButtons() {
      return (this.language.buttons || []).filter((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER')
    },
    quickReplies() {
      return (this.language.buttons || []).filter((b) => b.type === 'QUICK_REPLY')
    },
  },
  methods: {
    placeholderFor(btn) {
      return btn.type === 'URL' ? 'Visit website' : 'Call us'
    },
  },
}
</script>

<style scoped>
.wa-preview {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #d9e0da;
  background: #efe7dd;
  font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
  max-width: 380px;
  margin: 0 auto;
}

.wa-preview-topbar {
  display: flex;
  align-items: center;
  background: #1B6B3F;
  color: #fff;
  padding: 8px 12px;
  gap: 6px;
}

.wa-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 2px;
}

.wa-topbar-title {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.1;
}

.wa-topbar-sub {
  font-size: 11px;
  opacity: 0.85;
}

.wa-topbar-lang {
  font-size: 12px;
  opacity: 0.9;
}

.wa-chat {
  padding: 14px 12px 18px;
  min-height: 220px;
  background-color: #efe7dd;
  background-image: radial-gradient(rgba(0, 0, 0, 0.035) 1px, transparent 1px);
  background-size: 14px 14px;
}

.wa-bubble {
  background: #fff;
  border-radius: 10px;
  padding: 6px 8px 4px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.12);
  max-width: 96%;
  position: relative;
}

.wa-header-text {
  font-weight: 700;
  font-size: 14.5px;
  margin: 2px 0 6px;
  color: #111;
}

.wa-media {
  border-radius: 8px;
  overflow: hidden;
  margin: 0 0 8px;
  background: #e2e2e2;
}

.wa-media-img {
  display: block;
  width: 100%;
  max-height: 180px;
  object-fit: cover;
}

.wa-media-placeholder {
  height: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  gap: 4px;
  font-size: 12px;
}

.wa-body {
  font-size: 14px;
  color: #111;
  line-height: 1.4;
  white-space: normal;
  word-wrap: break-word;
}

.wa-body-empty {
  color: #9aa39c;
  font-style: italic;
}

.wa-body :deep(.wa-var) {
  background: #FBF4DF;
  color: #7a5c00;
  border-radius: 3px;
  padding: 0 3px;
}

.wa-footer {
  font-size: 12px;
  color: #6b7a6f;
  margin-top: 6px;
}

.wa-time {
  font-size: 10.5px;
  color: #8a948c;
  text-align: right;
  margin-top: 2px;
}

.wa-button {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #0b84c5;
  padding: 9px 8px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.12);
  max-width: 96%;
}

.wa-button-cta {
  margin-top: 4px;
}

.wa-quick-replies {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
  max-width: 96%;
}

.wa-button-qr {
  flex: 1 1 45%;
  padding: 8px;
}
</style>
